import React from 'lib/react';
import {
  EVGrid,
  EVSkeleton,
  EVPaper,
  EVTypography,
  EVButton,
  EVLink,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import clsx from 'lib/clsx';
import { LEGACY_PORTAL_LINKS } from 'constants/AppConstants';
import PaymentInformationLoader from 'commonComponents/paymentInformation/Components/PaymentInformationLoader/PaymentInformationLoader.component';
import FinalizeOrderLoaderStyles from './FinalizeOrderLoaderStyles';
import FinalizeOrderExpansionPanel from './components/FinalizeOrderExpansionPanel.component';

function FinalizeOrderLoader() {
  const classes = FinalizeOrderLoaderStyles();
  const { t } = useTranslation();
  const getPanelHeader = (header) => (<EVGrid>{header}</EVGrid>);

  const orderDetailLoader = () => (
    <EVPaper className={classes.orderDetailLoader}>
      <EVGrid container className={classes.orderDetailLoaderGrid}>
        <EVSkeleton style={{ height: '400px', borderRadius: '6px' }} animation="wave" variant="rect" />
      </EVGrid>
      <EVGrid container className={classes.orderDetailLoaderGrid}>
        <EVGrid container style={{ flexDirection: 'column', gap: '10px' }}>
          <EVSkeleton style={{ height: '18px', width: '92px', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '16px', width: '73px', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
        <EVGrid container style={{ flexDirection: 'column', gap: '10px' }}>
          <EVSkeleton className={classes.orderDetailLoaderSkeletonA} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '48px', width: '273px', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
      </EVGrid>
    </EVPaper>
  );

  const orderSummaryLoader = () => (
    <EVPaper classes={{ root: classes.orderSummary }} square={false}>
      <EVTypography className={classes.orderSummaryHeading}>{t('finalize_order__order_summary')}</EVTypography>
      <EVGrid className={clsx(classes.flexColumn, classes.terms)}>
        <EVGrid container style={{ gap: '10px', flexDirection: 'column' }}>
          <EVGrid container style={{ gap: '10px', alignItems: 'flex-start', alignSelf: 'stretch' }}>
            <EVSkeleton style={{ height: '24px', width: '240px', borderRadius: '6px' }} animation="wave" variant="rect" />
            <EVSkeleton style={{ height: '24px', flex: '1 0 0', borderRadius: '6px' }} animation="wave" variant="rect" />
          </EVGrid>
          <EVGrid container className={classes.orderDetailLoaderSummaryGrid}>
            <EVSkeleton style={{ height: '18px', width: '180px', borderRadius: '6px' }} animation="wave" variant="rect" />
            <EVSkeleton style={{ height: '18px', width: '82px', borderRadius: '6px' }} animation="wave" variant="rect" />
          </EVGrid>
        </EVGrid>
        <EVGrid container style={{ gap: '10px', flexDirection: 'column' }}>
          <EVSkeleton style={{ height: '18px', width: '136px', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVGrid container style={{ gap: '10px', alignItems: 'flex-start', alignSelf: 'stretch' }}>
            <EVSkeleton style={{ height: '44px', width: '172px', borderRadius: '6px' }} animation="wave" variant="rect" />
            <EVSkeleton style={{ height: '44px', width: '85px', borderRadius: '6px' }} animation="wave" variant="rect" />
          </EVGrid>
        </EVGrid>
        <EVGrid container style={{ gap: '72px', alignItems: 'flex-start', alignSelf: 'stretch' }}>
          <EVSkeleton style={{ height: '24px', flex: '1 0 0', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '18px', width: '82px', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
        <EVGrid container style={{ gap: '72px', alignItems: 'flex-start', alignSelf: 'stretch' }}>
          <EVSkeleton style={{ height: '24px', flex: '1 0 0', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '18px', width: '82px', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
        <EVGrid container className={classes.orderDetailLoaderSummaryGridA}>
          <EVSkeleton style={{ height: '24px', flex: '1 0 0', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '18px', width: '82px', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
        <EVTypography>
          {t('finalize_order__order_summary__payment_terms_of_use_1')}
          <EVLink target="_blank" href={LEGACY_PORTAL_LINKS.termsOfUse}>{t('finalize_order__order_summary__payment_terms_of_use_2')}</EVLink>
        </EVTypography>
      </EVGrid>
      <EVGrid container justifyContent="space-between" wrap="nowrap">
        <EVButton variant="outlined" disabled color="primary" className={classes.button}>
          {t('finalize_order__order_summary__payment_edit_order')}
        </EVButton>
        <EVButton id="submitOrderBtn" disabled variant="contained" style={{ backgroundColor: '#E6EAF1' }} className={classes.button}>
          {t('finalize_order__order_summary__payment_submit_order')}
        </EVButton>
      </EVGrid>
    </EVPaper>
  );

  const specialInstructionLoader = () => (
    <FinalizeOrderExpansionPanel
      defaultExpanded
      panelSummary={getPanelHeader(t('finalize_order__include_special_instructions'))}
    >
      <EVGrid container style={{ flexDirection: 'column', gap: '24px' }}>
        <EVSkeleton style={{ height: '80px', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVSkeleton style={{ height: '18px', width: '199px', borderRadius: '6px' }} animation="wave" variant="rect" />
      </EVGrid>
    </FinalizeOrderExpansionPanel>
  );

  const claimInformationLoader = () => (
    <FinalizeOrderExpansionPanel
      expanded={false}
      panelSummary={getPanelHeader(t('finalize_order__claim_information'))}
    >
      <EVGrid container style={{ flexDirection: 'column', gap: '24px' }}>
        <EVSkeleton style={{ height: '80px', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVSkeleton style={{ height: '18px', width: '199px', borderRadius: '6px' }} animation="wave" variant="rect" />
      </EVGrid>
    </FinalizeOrderExpansionPanel>
  );

  const emailRecipientsLoader = () => (
    <FinalizeOrderExpansionPanel
      panelSummary={getPanelHeader(t('finalize_order__email_recipients'))}
      defaultExpanded
    >
      <EVGrid container style={{ flexDirection: 'column', gap: '16px' }}>
        <EVSkeleton style={{ height: '18px', borderRadius: '6px' }} animation="wave" variant="rect" />
        <EVGrid container style={{ flexDirection: 'column', gap: '8px' }}>
          <EVSkeleton style={{ height: '24px', width: '215px', borderRadius: '6px' }} animation="wave" variant="rect" />
          <EVSkeleton className={classes.emailRecipientsSkeleton} animation="wave" variant="rect" />
          <EVSkeleton style={{ height: '18px', width: '323px', borderRadius: '6px' }} animation="wave" variant="rect" />
        </EVGrid>
      </EVGrid>
    </FinalizeOrderExpansionPanel>
  );
  const substitutionsAndCustomization = () => (
    <FinalizeOrderExpansionPanel
      panelSummary={getPanelHeader(t('finalize_order__substitutions_and_customization'))}
    />
  );

  const paymentInformationLoader = () => (
    <FinalizeOrderExpansionPanel
      defaultExpanded
      panelSummary={getPanelHeader(t('finalize_order__payment_information'))}
    >
      <PaymentInformationLoader splitCardContainer fullWidthInputFields />
    </FinalizeOrderExpansionPanel>
  );
  return (
    <EVGrid container className={classes.finalizeOrder}>
      <EVGrid item xs={12} sm={12} md={8} lg={8} className={classes.finalizeOrderPanels}>
        {orderDetailLoader()}
        {specialInstructionLoader()}
        {claimInformationLoader()}
        {emailRecipientsLoader()}
        {substitutionsAndCustomization()}
        {paymentInformationLoader()}
      </EVGrid>
      {orderSummaryLoader()}
    </EVGrid>
  );
}

export default FinalizeOrderLoader;
